import { v4 as uuidv4 } from 'uuid';
import { createSlice } from '@reduxjs/toolkit';
import { MAX_EMAILS_AMOUNT, MAX_PASSENGERS_AMOUNT, MIN_EMAILS_AMOUNT, MIN_PASSENGERS_AMOUNT, } from '@/libs/constants';
import { DEFAULT_FIELD_ERROR } from '@/libs/validation/constants';
import { getDeepCopy } from '@/libs/helpers/getDeepCopy';
import { FORMS_STATE_NAMESPACE } from './consts';
import { InsurerField, PassengerField, } from './interface';
const DEFAULT_PASSENGER_AGE = 30;
const getKeys = (target) => Object.keys(target);
const getObjectFilledWithValue = (fields, value) => getKeys(fields).reduce((object, field) => ((object[field] = value), object), {});
export const defaultPassenger = ({ age = DEFAULT_PASSENGER_AGE, isNew = false, } = {}) => {
    return {
        _uuid: uuidv4(),
        ...getObjectFilledWithValue(PassengerField, ''),
        fieldsErrors: getObjectFilledWithValue(PassengerField, DEFAULT_FIELD_ERROR),
        fieldsDirty: getObjectFilledWithValue(PassengerField, false),
        age,
        isNew,
    };
};
const defaultEmail = () => ({
    _uuid: uuidv4(),
    value: '',
    errorMessage: DEFAULT_FIELD_ERROR,
    isDirty: false,
});
export const formsInitialState = {
    passengers: [defaultPassenger({ isNew: true })],
    insurer: {
        ...getObjectFilledWithValue(InsurerField, ''),
        fieldsErrors: getObjectFilledWithValue(InsurerField, DEFAULT_FIELD_ERROR),
        fieldsDirty: getObjectFilledWithValue(InsurerField, false),
        phoneCountryCode: '7',
    },
    emails: [defaultEmail()],
    isFormValidated: false,
    isFormDirty: false,
    hasAgreePersonalData: { value: false, isDirty: false },
};
const META = {
    startRecalculation: true,
    forceRecalculation: true,
    checkPassengersBirthdays: false,
};
const formsSlice = createSlice({
    name: FORMS_STATE_NAMESPACE,
    initialState: formsInitialState,
    reducers: {
        _resetForms: (state, action) => {
            const { passengersAmount, emailsAmount } = action.payload;
            const newState = getDeepCopy({
                ...formsInitialState,
                passengers: Array.from({
                    length: Math.min(Math.max(passengersAmount, MIN_PASSENGERS_AMOUNT), MAX_PASSENGERS_AMOUNT),
                }, () => defaultPassenger()),
                emails: Array.from({
                    length: Math.min(Math.max(emailsAmount, MIN_EMAILS_AMOUNT), MAX_EMAILS_AMOUNT),
                }, () => defaultEmail()),
            });
            return newState;
        },
        addPassenger: {
            reducer: (state, action) => {
                if (state.passengers.length < MAX_PASSENGERS_AMOUNT) {
                    state.passengers.push(defaultPassenger({ age: action?.payload, isNew: true }));
                }
            },
            prepare: (payload) => ({ payload, meta: META }),
        },
        removePassenger: {
            reducer: (state, action) => {
                if (state.passengers.length > MIN_PASSENGERS_AMOUNT) {
                    state.passengers = state.passengers.filter((passenger) => passenger._uuid !== action.payload);
                }
            },
            prepare: (payload) => ({ payload, meta: META }),
        },
        _setPassengerField: (state, action) => {
            const { _uuid, field, value } = action.payload;
            const passenger = state.passengers.find((passenger) => passenger._uuid === _uuid);
            if (passenger) {
                passenger[field] = value;
            }
        },
        _setPassengerAge: {
            reducer: (state, action) => {
                const { _uuid, age } = action.payload;
                const passenger = state.passengers.find((passenger) => passenger._uuid === _uuid);
                if (passenger) {
                    passenger.age = age;
                    passenger.isNew = false;
                }
            },
            prepare: (payload, noMeta) => ({
                payload,
                meta: noMeta
                    ? undefined
                    : {
                        ...META,
                        META_PARAM_TO_FORCE_RECALCULATION_ON_BIRTHDAY_COMPLETE: true,
                    },
            }),
        },
        _setPassengerFieldError: (state, action) => {
            const { _uuid, field, error } = action.payload;
            const passenger = state.passengers.find((passenger) => passenger._uuid === _uuid);
            if (passenger) {
                passenger.fieldsErrors[field] = error;
            }
        },
        setPassengerFieldDirty: (state, action) => {
            const { _uuid, field } = action.payload;
            const passenger = state.passengers.find((passenger) => passenger._uuid === _uuid);
            if (passenger) {
                passenger.fieldsDirty[field] = true;
            }
        },
        _setInsurerField: (state, action) => {
            const { field, value } = action.payload;
            state.insurer[field] = value;
        },
        _setInsurerFieldError: (state, action) => {
            const { field, error } = action.payload;
            state.insurer.fieldsErrors[field] = error;
        },
        setInsurerFieldDirty: (state, action) => {
            state.insurer.fieldsDirty[action.payload] = true;
        },
        setPhoneCountryCode: (state, action) => {
            state.insurer.phoneCountryCode = action.payload;
            state.insurer.phoneBody = '';
        },
        addEmail: (state) => {
            if (state.emails.length < MAX_EMAILS_AMOUNT)
                state.emails.push(defaultEmail());
        },
        removeEmail: (state, action) => {
            if (state.emails.length > MIN_EMAILS_AMOUNT) {
                state.emails = state.emails.filter((email) => email._uuid !== action.payload);
            }
        },
        _setEmailValue: (state, action) => {
            const { _uuid, value } = action.payload;
            const email = state.emails.find((email) => email._uuid === _uuid);
            if (email) {
                email.value = value;
            }
        },
        _setEmailError: (state, action) => {
            const { _uuid, error } = action.payload;
            const email = state.emails.find((email) => email._uuid === _uuid);
            if (email) {
                email.errorMessage = error;
            }
        },
        setEmailDirty: (state, action) => {
            const email = state.emails.find((email) => email._uuid === action.payload);
            if (email) {
                email.isDirty = true;
            }
        },
        _setFormValidated: (state, action) => {
            state.isFormValidated = action.payload;
        },
        setFormDirty: (state, action) => {
            state.isFormDirty = action.payload ?? true;
        },
        setHasAgreePersonalData: (state, action) => {
            state.hasAgreePersonalData.value = action.payload;
            state.hasAgreePersonalData.isDirty = true;
        },
    },
});
export const { actions: formsActions, reducer: formsReducer } = formsSlice;
export { FORMS_STATE_NAMESPACE };
export * from './consts';
